import React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import cn from 'clsx';

import { CrossIcon, PictureIcon } from '@components/elements/Icons';

type ImagePickerButtonNewProps = {
  value?: File;
  imageSrc?: string;
  onChange?: (file?: File) => void;
  disabled?: boolean;
  length?: number;
  multiple?: boolean;
  accept?: string;
  className?: string;
};

const ImagePickerButtonNew = React.forwardRef<
  HTMLInputElement,
  ImagePickerButtonNewProps
>(
  (
    {
      value,
      imageSrc,
      onChange,
      disabled = false,
      length = 1,
      accept = 'image/*',
      className,
    },
    ref
  ) => {
    const [files, setFiles] = useState<FileList | File[]>();
    const [image, setImage] = useState<string | undefined>();
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      // Initial value may be ''
      if (value && (value as any) !== '') setFiles([value]);
      else setFiles([]);
    }, [value, setFiles]);

    useEffect(() => {
      if ((files?.length || 0) > 0) {
        const file = files?.[0];
        if (!file) return;

        if (typeof file === 'string') return setImage(file);
        try {
          if (typeof file.name !== 'string') {
            return;
          }
          const reader = new FileReader();

          reader.onload = function (e) {
            setImage(e.target?.result?.toString());
          };
          reader.readAsDataURL(file);
        } catch (err) {
          console.log('err reading image file', err);
        }
      }
    }, [files]);

    const _onChange = (e) => {
      setFiles(e.target.files);
      const file = e.target.files?.[0];
      onChange?.(file);
    };

    const clearImage = () => {
      onChange?.(undefined);
      setImage(undefined);
      setFiles(undefined);
    };

    const onClick = useCallback(() => {
      inputRef?.current?.dispatchEvent?.(new MouseEvent('click'));
    }, [inputRef]);

    const rootCn = cn(
      'h-14 w-16 transition-colors border-gray-dark rounded-md text-gray-dark relative',
      { 'border-3 hover:bg-gray-dark hover:text-white': !image },
      className
    );

    const btnCn =
      'h-full w-full focus:outline-none flex items-center justify-center';
    return (
      <div className={rootCn}>
        <div className="invisible w-0 h-0">
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            multiple={length > 1}
            onChange={_onChange}
          />
        </div>
        {image || imageSrc ? (
          <>
            <button
              type="button"
              className={btnCn}
              onClick={disabled ? undefined : onClick}
            >
              <img
                className="p-2"
                src={image || imageSrc}
                alt="selected-file"
              />
            </button>
            <button
              type="button"
              className="absolute top-0 right-0 bg-white rounded-full shadow-lg"
              onClick={clearImage}
            >
              <CrossIcon className="w-4 h-4" />
            </button>
          </>
        ) : (
          <button
            type="button"
            className={btnCn}
            onClick={disabled ? undefined : onClick}
          >
            <PictureIcon />
          </button>
        )}
      </div>
    );
  }
);

export default ImagePickerButtonNew;
